import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    FiLogOut, FiUser, FiHome, FiUsers, FiSettings,
    FiMenu, FiX, FiFileText, FiTruck,
    FiEdit3, FiBarChart2, FiCalendar,
    FiCreditCard,
    FiPieChart,
    FiGrid,
    FiMessageSquare, FiBell,
    FiCheckCircle,
    FiSlash,
    FiMoreHorizontal
} from "react-icons/fi";
import { RiUserSettingsLine } from "react-icons/ri";
import { useAuth } from "../../contexts/AuthContext";
import { getAccountBlockReason } from "../../lib/accountAccess";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import MessageMenu from '../messaging/MessageMenu';
import NotificationMenu from '../notification/NotificationMenu';

import { useMessageNotifications } from '../../hooks/useMessageNotifications';
import { useNotificationBadge } from '../../hooks/useNotificationBadge';

import "../../App.css"
import UserDetails from "../users/UserDetails";
import { hasPermission } from "../../lib/permissions";
import AboutNotifyButton from "../common/AboutNotifyButton";

const Navbar = ({ isSidebarOpen, setIsSidebarOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userProfile, company, signOut, loading } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [userDetailsOpen, setUserDetailsOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const [showMessageMenu, setShowMessageMenu] = useState(false);
    const [showNotificationMenu, setShowNotificationMenu] = useState(false);

    const {
        unreadCount: unreadMessageCount,
        refreshCount: refreshMessageBadge,
    } = useMessageNotifications(userProfile?.id);
    const {
        unreadCount: unreadNotificationCount,
        refreshCount: refreshNotificationBadge,
        latestNotification,
        dismissLatestNotification,
        updateSoundSettings: updateNotificationSoundSettings,
    } = useNotificationBadge(userProfile?.id);

    const companySlug = company?.slug;

    // Ref on the desktop Messages button — used to anchor the toast bubble
    // const msgButtonRef = useRef(null);
    // const [msgButtonRect, setMsgButtonRect] = useState(null);

    // Keep rect up-to-date whenever the toast appears or sidebar toggles
    // useEffect(() => {
    //     if (!latestMessage || !msgButtonRef.current) return;
    //     const rect = msgButtonRef.current.getBoundingClientRect();
    //     setMsgButtonRect(rect);
    // }, [latestMessage, isSidebarOpen]);

    // Auto-dismiss the new-message toast after 5 seconds
    // useEffect(() => {
    //     if (!latestMessage) return;
    //     const timer = setTimeout(() => dismissLatestMessage(), 5000);
    //     return () => clearTimeout(timer);
    // }, [latestMessage]);

    // Ref on the desktop Notifications button — used to anchor the notification toast
    const notifButtonRef = useRef(null);
    const [notifButtonRect, setNotifButtonRect] = useState(null);

    // Keep rect up-to-date whenever the toast appears or sidebar toggles
    useEffect(() => {
        if (!latestNotification || !notifButtonRef.current) return;
        const rect = notifButtonRef.current.getBoundingClientRect();
        setNotifButtonRect(rect);
    }, [latestNotification, isSidebarOpen]);

    // Auto-dismiss the notification toast after 5 seconds
    useEffect(() => {
        if (!latestNotification) return;
        const timer = setTimeout(() => dismissLatestNotification(), 5000);
        return () => clearTimeout(timer);
    }, [latestNotification]);

    // Check if device is mobile
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Navigation items based on user role
    const getNavigationItems = () => {
        const commonItems = [
            { name: 'Dashboard', path: `/${companySlug}/dashboard`, icon: FiHome },
        ];

        const adminItems = [
            ...commonItems,
            { name: 'Jobs', path: `/${companySlug}/assignment/list`, icon: FiFileText },

            ...(userProfile?.permissions?.includes("SEEPAGE_MYJOBS")
                ? [{ name: 'My Jobs', path: `/${companySlug}/assignment/my-assignments`, icon: FiEdit3 }]
                : []),

            { name: 'Schedule', path: `/${companySlug}/schedule`, icon: FiCalendar },
            { name: 'Customers', path: `/${companySlug}/customers`, icon: FiUsers },
            { name: 'Users', path: `/${companySlug}/users`, icon: RiUserSettingsLine },

            ...(
                !userProfile?.permissions?.includes("NA_ANALYTICS") ||
                    userProfile?.permissions?.includes("SEEPAGE_ANALYTICS")
                    ? [{ name: 'Analytics', path: `/${companySlug}/analytics`, icon: FiBarChart2 }]
                    : []
            ),

            // ...(
            //     !userProfile?.permissions?.includes("NA_SETTINGS") ||
            //         userProfile?.permissions?.includes("SEEPAGE_SETTINGS")
            //         ? [{ name: 'Settings', path: `/${companySlug}/settings`, icon: FiSettings }]
            //         : []
            // ),

            // { name: 'Analytics', path: `/${companySlug}/analytics`, icon: FiBarChart2 },
            // { name: 'Settings', path: `/${companySlug}/settings`, icon: FiSettings, disabled: true },
        ];


        const superAdminItems = [
            ...commonItems,
            { name: 'Jobs', path: `/${companySlug}/assignment/list`, icon: FiFileText },

            ...(userProfile?.permissions?.includes("SEEPAGE_MYJOBS")
                ? [{ name: 'My Jobs', path: `/${companySlug}/assignment/my-assignments`, icon: FiEdit3 }]
                : []),

            { name: 'Schedule', path: `/${companySlug}/schedule`, icon: FiCalendar },
            { name: 'Customers', path: `/${companySlug}/customers`, icon: FiUsers },
            { name: 'Users', path: `/${companySlug}/users`, icon: RiUserSettingsLine },
            { name: 'Analytics', path: `/${companySlug}/analytics`, icon: FiBarChart2 },
            { name: 'Settings', path: `/${companySlug}/settings`, icon: FiSettings },
        ];

        const designerItems = [
            ...commonItems,
            { name: 'Jobs', path: `/${companySlug}/assignment/list`, icon: FiFileText },
            { name: 'My Jobs', path: `/${companySlug}/assignment/my-assignments`, icon: FiEdit3 },
            { name: 'Schedule', path: `/${companySlug}/schedule`, icon: FiCalendar },
            { name: 'Customers', path: `/${companySlug}/customers`, icon: FiUsers },
            { name: 'Users', path: `/${companySlug}/users`, icon: RiUserSettingsLine },

        ];

        const estimatorItems = [
            ...commonItems,
            { name: 'Jobs', path: `/${companySlug}/assignment/list`, icon: FiFileText },
            { name: 'My Jobs', path: `/${companySlug}/assignment/my-assignments`, icon: FiEdit3 },
            { name: 'Schedule', path: `/${companySlug}/schedule`, icon: FiCalendar },
            { name: 'Customers', path: `/${companySlug}/customers`, icon: FiUsers },
            { name: 'Users', path: `/${companySlug}/users`, icon: RiUserSettingsLine },

        ];

        const driverItems = [
            ...commonItems,
            { name: 'My Deliveries', path: `/${companySlug}/my-deliveries`, icon: FiTruck },
            { name: 'Completed Deliveries', path: `/${companySlug}/my-deliveries/completed`, icon: FiCheckCircle },
            { name: 'Users', path: `/${companySlug}/users`, icon: FiUsers },

        ];

        const appOwnerItems = [
            { name: 'Dashboard', path: `/${companySlug}/dashboard`, icon: FiHome },
            { name: 'Manage Companies', path: `/${companySlug}/companies`, icon: FiGrid },
            { name: 'Manage Users', path: `/${companySlug}/users`, icon: FiUsers },
            { name: 'System Analytics', path: `/${companySlug}/analytics`, icon: FiPieChart },
            { name: 'Activity Logs', path: `/${companySlug}/activity`, icon: FiFileText },
            { name: 'Billing & Plans', path: `/${companySlug}/billing`, icon: FiCreditCard },
        ]

        if (userProfile?.status !== "active") return [
            { name: `Account ${userProfile?.status}`, path: ``, icon: FiSlash, disabled: true },
        ]

        if (companySlug === "notify") return appOwnerItems;

        switch (userProfile?.role) {
            case 'super_admin':
                return superAdminItems;
            case 'admin':
                return adminItems;
            case 'designer':
                return designerItems;
            case 'estimator':
                return estimatorItems;
            case 'driver':
                return driverItems;
            default:
                return commonItems;
        }
    };

    const navigationItems = getNavigationItems();

    // Get user type styling
    const getUserTypeStyle = () => {
        switch (userProfile?.role) {
            case 'super_admin':
            case 'admin':
                return {
                    bg: 'bg-gradient-to-br from-blue-500 to-blue-600',
                    text: 'text-blue-600',
                    lightBg: 'bg-blue-50',
                    darkBg: 'bg-blue-900/20',
                };
            case 'designer':
                return {
                    bg: 'bg-gradient-to-br from-green-500 to-green-600',
                    text: 'text-green-600',
                    lightBg: 'bg-green-50',
                    darkBg: 'bg-green-900/20',
                };
            case 'estimator':
                return {
                    bg: 'bg-gradient-to-br from-purple-500 to-purple-600',
                    text: 'text-purple-600',
                    lightBg: 'bg-purple-50',
                    darkBg: 'bg-purple-900/20',
                };
            case 'driver':
                return {
                    bg: 'bg-gradient-to-br from-red-500 to-red-600',
                    text: 'text-red-600',
                    lightBg: 'bg-red-50',
                    darkBg: 'bg-red-900/20',
                };
            case 'app_owner':
                return {
                    bg: 'bg-gradient-to-br from-rose-500 to-rose-600',
                    text: 'text-rose-600',
                    lightBg: 'bg-rose-50',
                    darkBg: 'bg-rose-900/20',
                };
            default:
                return {
                    bg: 'bg-gradient-to-br from-gray-500 to-gray-600',
                    text: 'text-gray-600',
                    lightBg: 'bg-gray-50',
                    darkBg: 'bg-gray-900/20',
                };
        }
    };

    const userStyle = getUserTypeStyle();

    const handleLogout = async () => {
        try {
            // Close any open menus first
            setIsOpen(false);
            setIsMobileMenuOpen(false);
            setShowMessageMenu(false);
            setShowNotificationMenu(false);

            // Sign out
            const { error } = await signOut();

            if (error) {
                console.error('Logout error:', error);
                return;
            }

            // Use window.location for a full page reload to ensure clean state
            // This is especially important for iOS Safari which can cache state
            window.location.href = '/login';
        } catch (error) {
            console.error('Logout error:', error);
            // Fallback to window.location even on error
            window.location.href = '/login';
        }
    };

    const isActivePath = (path) => {
        // Exact match for the path
        if (location.pathname === path) return true;

        // For paths that might have sub-routes, only match if the current path starts with the route
        // BUT exclude cases where we're in a more specific sub-route
        // Example: /my-deliveries should NOT match when we're in /my-deliveries/completed
        const currentPath = location.pathname;
        const pathWithSlash = path + '/';

        // Check if any navigation item has a path that:
        // 1. Starts with the current candidate path
        // 2. Is longer than the candidate path
        // 3. Matches the current location
        const hasMoreSpecificMatch = navigationItems.some(item =>
            item.path.startsWith(path) &&
            item.path.length > path.length &&
            (currentPath === item.path || currentPath.startsWith(item.path + '/'))
        );

        // If there's a more specific match, don't activate this path
        if (hasMoreSpecificMatch) return false;

        // Otherwise, use standard prefix matching
        return currentPath.startsWith(pathWithSlash);
    };

    // Get primary navigation items for bottom bar (first 4 items)
    const getPrimaryNavItems = () => {
        return navigationItems.slice(0, 4);
    };

    // Show skeleton navbar while loading
    if (loading) {
        return (
            <>
                {/* Desktop Skeleton */}
                <aside
                    className={`hidden md:block fixed top-0 left-0 z-50 h-screen bg-white dark:bg-zinc-900 border-r border-gray-200 dark:border-zinc-800 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'w-64' : 'w-20'}`}
                >
                    <div className="flex flex-col h-full">
                        {/* Logo Section Skeleton */}
                        <div className="flex items-center justify-center h-20 px-4 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
                            {isSidebarOpen ? (
                                <div className="flex items-center space-x-3 w-full">
                                    <Skeleton circle width={40} height={40} />
                                    <Skeleton width={100} height={24} />
                                </div>
                            ) : (
                                <Skeleton circle width={24} height={24} />
                            )}
                        </div>

                        {/* Company Info Skeleton */}
                        <div className="px-4 py-4 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
                            <div className={`flex items-center ${!isSidebarOpen ? 'justify-center' : 'space-x-3'}`}>
                                <Skeleton circle width={32} height={32} />
                                {isSidebarOpen && (
                                    <div className="flex-1">
                                        <Skeleton width={120} height={16} className="mb-1" />
                                        <Skeleton width={80} height={12} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Navigation Links Skeleton */}
                        <div className="flex-1 overflow-y-auto py-4 px-3">
                            <div className="space-y-1">
                                {[...Array(5)].map((_, index) => (
                                    <div key={index} className={`flex items-center ${!isSidebarOpen ? 'justify-center h-11 w-11 mx-auto' : 'h-11 px-3 space-x-3'}`}>
                                        <Skeleton circle={!isSidebarOpen} width={isSidebarOpen ? 20 : 25} height={isSidebarOpen ? 20 : 25} />
                                        {isSidebarOpen && <Skeleton width={100} height={16} />}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Messages & Notifications Skeleton */}
                        <div className="border-t border-gray-200 dark:border-zinc-800 pt-2 space-y-1 px-3">
                            {[...Array(2)].map((_, index) => (
                                <div key={index} className={`flex items-center ${!isSidebarOpen ? 'justify-center h-11 w-11 mx-auto' : 'h-11 px-3 space-x-3'}`}>
                                    <Skeleton circle={!isSidebarOpen} width={isSidebarOpen ? 20 : 25} height={isSidebarOpen ? 20 : 25} />
                                    {isSidebarOpen && <Skeleton width={80} height={16} />}
                                </div>
                            ))}
                        </div>

                        {/* User Profile Skeleton */}
                        <div className="border-t border-gray-200 dark:border-zinc-800 p-4 space-y-2 flex-shrink-0">
                            <div className={`flex flex-col items-center ${!isSidebarOpen ? 'justify-center' : 'space-x-3'}`}>
                                <Skeleton circle width={40} height={40} />
                                <Skeleton circle width={40} height={40} />
                                {isSidebarOpen && (
                                    <div className="flex-1">
                                        <Skeleton width={120} height={16} className="mb-1" />
                                        <Skeleton width={80} height={12} />
                                    </div>
                                )}
                            </div>
                            {isSidebarOpen && (
                                <Skeleton width="100%" height={44} borderRadius={12} />
                            )}
                        </div>
                    </div>
                </aside>

                {/* Mobile Bottom Nav Skeleton */}
                <div className="mobile-bottom-nav md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-zinc-900 border-t border-gray-200 dark:border-zinc-800">
                    <div className="flex justify-around items-center px-2 py-2 safe-area-inset-bottom">
                        {[...Array(5)].map((_, index) => (
                            <div key={index} className="flex flex-col items-center justify-center w-16 h-14">
                                <Skeleton circle width={24} height={24} />
                                <Skeleton width={40} height={10} className="mt-1" />
                            </div>
                        ))}
                    </div>
                </div>
            </>
        );
    }

    // Account is blocked (subscription issue or inactive user) — NotActiveAccount
    // is about to take over the whole screen. This sidebar renders at z-50, which
    // would otherwise float above that fixed, unlayered overlay, so render nothing
    // instead of leaving a functional nav on top of a blocked screen.
    if (getAccountBlockReason({ userProfile, company })) {
        return null;
    }

    return (
        <>
            {/* Desktop Sidebar - Hidden on mobile */}
            <aside
                className={`hidden md:block fixed top-3 left-3 h-[calc(100vh-1.5rem)] rounded-xl shadow-lg z-50 bg-zinc-800 dark:bg-zinc-900 border-r border-gray-200 dark:border-zinc-800 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'w-64' : 'w-[3.7rem]'
                    }`}
            >
                <div className="flex flex-col h-full">
                    {/* Section 1 - Logo & Toggle */}
                    <div className="flex items-center justify-center h-20 px-4 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
                        {isSidebarOpen ? (
                            <>
                                {/* <a href="https://notifyapp.com.au" target="_blank" rel="noopener noreferrer">
                                    <div className="flex items-center space-x-3 overflow-hidden transition-opacity duration-200 w-36">
                                        <img className="-ml-2" src={process.env.PUBLIC_URL + '/assets/notify-cropped-wide.png'} alt="" />
                                    </div>
                                </a> */}

                                {company?.subscription_plan == "enterprise" && company?.enterprise_logo_url?.length > 0 ?
                                    <div className="h-full w-full">
                                        <div className="h-full flex items-center space-x-3 overflow-hidden transition-opacity duration-200 w-full select-none pointer-events-none">
                                            <img className="-ml-2 object-contain !w-auto !h-full" src={company.enterprise_logo_url} alt="" />
                                        </div>
                                    </div>
                                    :
                                    <div className="">
                                        <div className="flex items-center space-x-3 overflow-hidden transition-opacity duration-200 h-auto w-28 select-none pointer-events-none">
                                            <img className="" src={process.env.PUBLIC_URL + '/assets/wide-logo.svg'} alt="" />
                                        </div>
                                    </div>
                                }




                                <button
                                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors flex-shrink-0 ml-auto"
                                >
                                    <FiX className="w-5 h-5 text-zinc-300 dark:text-gray-400" />
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors flex-shrink-0"
                            >
                                <FiMenu className="w-5 h-5 text-zinc-300 dark:text-gray-400" />
                            </button>
                        )}
                    </div>

                    {/* Section 2 - Company Info */}
                    <div className="px-2 py-3 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
                        <button
                            className={`flex items-center rounded-xl hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all group ${!isSidebarOpen ? 'h-10 w-10 mx-auto justify-center' : 'w-full h-11 px-3 gap-3'
                                }`}
                            title={!isSidebarOpen ? "Click to change company" : ""}
                        >
                            {company?.logo_url ? (
                                <img
                                    src={company.logo_url}
                                    alt={company.name}
                                    className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                                />
                            ) : (
                                <div className={`w-8 h-8 ${userStyle.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                    <span className="text-white font-semibold text-sm">
                                        {company?.name?.charAt(0) || 'C'}
                                    </span>
                                </div>
                            )}
                            <div className={`flex-1 text-left min-w-0 overflow-hidden transition-all duration-300 ${isSidebarOpen ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0'
                                }`}>
                                <div className="font-semibold text-zinc-100 dark:text-white truncate text-sm">
                                    {company?.name || 'Company'}
                                </div>
                            </div>
                        </button>
                    </div>

                    {/* Section 3 - Navigation Links (Scrollable) */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-zinc-700 scrollbar-track-transparent navbar-scroll">
                        <nav className="space-y-2">
                            {navigationItems.map((item, index) => {
                                const Icon = item.icon;
                                const isActive = isActivePath(item.path);

                                return (
                                    <div key={index} className="flex flex-col gap-1">
                                        <button
                                            key={item.path}
                                            onClick={() => navigate(item.path)}
                                            className={`flex items-center rounded-xl transition-all group
                                            ${item?.disabled ? 'opacity-60 cursor-not-allowed' : ''}
                                            ${isActive
                                                    ? `${userStyle.lightBg} dark:${userStyle.darkBg} ${userStyle.text}`
                                                    : 'text-zinc-300 dark:text-gray-300 hover:bg-zinc-700 dark:hover:bg-zinc-800'
                                                } ${!isSidebarOpen ? 'h-10 w-10 mx-auto justify-center' : 'w-full h-11 px-3 gap-3'}`}
                                            title={!isSidebarOpen ? item.name : ''}
                                            disabled={item?.disabled && true}
                                        >
                                            <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? userStyle.text : 'text-white group-hover:text-white'
                                                }`} />
                                            <span className={`font-medium truncate transition-all duration-300 text-sm ${isActive ? 'font-semibold' : ''
                                                } ${isSidebarOpen ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0 w-0'}`}>
                                                {item.name}
                                            </span>
                                            {isSidebarOpen && isActive && (
                                                <div className={`ml-auto w-1.5 h-1.5 rounded-full ${userStyle.bg} flex-shrink-0`}></div>
                                            )}
                                        </button>
                                        {!isSidebarOpen &&
                                            <small className="text-[10px] text-white self-start text-center truncate w-full">{item.name}</small>
                                        }
                                    </div>
                                );
                            })}
                        </nav>
                    </div>

                    {/* Section 3.5 - Messages & Notifications */}
                    <div className="border-t border-gray-200 dark:border-zinc-800 pt-2 my-2 space-y-1 px-2">
                        {/* Messages Button */}
                        <div className="flex flex-col gap-1">
                            <button
                                // ref={msgButtonRef}
                                onClick={() => {
                                    if (userProfile?.status === "active") {
                                        setShowMessageMenu(!showMessageMenu);
                                        setShowNotificationMenu(false);
                                    }
                                }}
                                className={`flex items-center rounded-xl transition-all group relative ${showMessageMenu
                                    ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 fill-purple-600'
                                    : unreadMessageCount > 0
                                        ? 'text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                                        : 'text-zinc-300 dark:text-gray-300 hover:bg-zinc-700 dark:hover:bg-zinc-800'
                                    } ${!isSidebarOpen ? 'h-10 w-10 mx-auto justify-center' : 'w-full h-11 px-3 gap-3'} ${userProfile?.status !== "active" && "cursor-not-allowed"}`}
                                title={!isSidebarOpen ? 'Messages' : ''}
                                disabled={userProfile?.status !== "active"}
                            >
                                <div className="relative">
                                    <FiMessageSquare className={`w-5 h-5 ${showMessageMenu ? "text-blue-600" : "text-white"} flex-shrink-0 ${unreadMessageCount > 0 ? 'msg-icon-buzz' : ''}`} />
                                    {unreadMessageCount > 0 && (
                                        <span className="msg-badge-breathe absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                            {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                                        </span>
                                    )}
                                </div>
                                {isSidebarOpen && (
                                    <span className="font-medium truncate text-sm">Messages</span>
                                )}
                                {isSidebarOpen && unreadMessageCount > 0 && (
                                    <span className="ml-auto px-2 py-0.5 bg-red-600 text-white text-xs rounded-full font-bold msg-badge-breathe">
                                        {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                                    </span>
                                )}
                            </button>
                            {!isSidebarOpen &&
                                <small className="text-[11px] text-white self-start text-center w-full truncate">Messages</small>
                            }
                        </div>

                        {/* Notifications Button */}
                        <div className="flex flex-col gap-1">
                            <button
                                ref={notifButtonRef}
                                onClick={() => {
                                    if (userProfile?.status === "active") {
                                        setShowNotificationMenu(!showNotificationMenu);
                                        setShowMessageMenu(false);
                                    }
                                }}
                                className={`flex items-center rounded-xl transition-all group relative ${showNotificationMenu
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600'
                                    : unreadNotificationCount > 0
                                        ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                                        : 'text-zinc-300 dark:text-gray-300 hover:bg-zinc-700 dark:hover:bg-zinc-800'
                                    } ${!isSidebarOpen ? 'h-10 w-10 mx-auto justify-center' : 'w-full h-11 px-3 gap-3'} ${userProfile?.status !== "active" && "cursor-not-allowed"}`}
                                title={!isSidebarOpen ? 'Notifications' : ''}
                                disabled={userProfile?.status !== "active"}
                            >
                                <div className="relative">
                                    <FiBell className={`w-5 h-5 ${showNotificationMenu ? "text-blue-600" : "text-white"}  flex-shrink-0 ${unreadNotificationCount > 0 ? 'notif-icon-buzz' : ''}`} />
                                    {unreadNotificationCount > 0 && (
                                        <span className="notif-badge-breathe absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                            {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                                        </span>
                                    )}
                                </div>
                                {isSidebarOpen && (
                                    <span className="font-medium truncate text-sm">Notifications</span>
                                )}
                                {isSidebarOpen && unreadNotificationCount > 0 && (
                                    <span className="ml-auto px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full font-bold notif-badge-breathe">
                                        {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                                    </span>
                                )}
                            </button>
                            {!isSidebarOpen &&
                                <small className="text-[11px] text-white self-start text-center w-full truncate">Notifications</small>
                            }
                        </div>

                    </div>


                    {/* Section 4 - User Profile & Logout */}
                    <div className="border-t border-gray-200 dark:border-zinc-800 px-2 py-3 space-y-3 flex-shrink-0">

                        {!isSidebarOpen &&
                            <div className="flex justify-center mb-1">
                                {company?.subscription_plan == "enterprise" && company?.enterprise_icon_url?.length > 0 ?
                                    <a href="#">
                                        <img className="w-8 h-8 object-contain rounded" src={company.enterprise_icon_url} alt="" />
                                    </a>
                                    :
                                    <a href="https://notifyapp.com.au" target="_blank" rel="noopener noreferrer">
                                        <img className="w-8 h-8 object-contain" src={process.env.PUBLIC_URL + '/icons/icon-192.png'} alt="" />
                                    </a>
                                }
                            </div>
                        }

                        {/* User Profile */}
                        <button
                            onClick={() => {
                                setUserDetailsOpen(true)
                            }} // navigate(`/${companySlug}/user/${userProfile?.username?.toLowerCase() || userProfile?.id}`)
                            className={`flex items-center rounded-xl hover:bg-zinc-700 dark:hover:bg-zinc-800 transition-all ${!isSidebarOpen ? 'h-12 w-10 mx-auto justify-center' : 'w-full h-14 px-3 gap-3'
                                }`}
                        >
                            <div className={`w-10 h-10 ${userStyle.bg} rounded-full flex items-center justify-center flex-shrink-0 shadow-lg ${userProfile?.status !== "active" && "bg-gradient-to-br from-gray-500 to-gray-600"}`}>
                                {userProfile?.avatar_url ? (
                                    <img
                                        src={userProfile.avatar_url}
                                        alt={userProfile.full_name || userProfile.username}
                                        className="w-10 h-10 rounded-full object-cover"
                                    />
                                ) : (
                                    <FiUser className="w-5 h-5 text-white" />
                                )}
                            </div>
                            <div className={`flex-1 text-left min-w-0 overflow-hidden transition-all duration-300 ${isSidebarOpen ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0'
                                }`}>
                                <div className="font-semibold text-zinc-100 dark:text-white truncate text-sm">
                                    {userProfile?.full_name || userProfile?.username}
                                </div>
                                <div className={`text-xs font-medium ${userStyle.text} uppercase truncate ${userProfile?.status !== "active" && "text-gray-500"}`}>
                                    {userProfile?.status !== "active" ?
                                        userProfile?.status
                                        :
                                        userProfile?.role?.split("_")?.join(" ") || "USER"
                                    }
                                </div>
                            </div>
                        </button>

                        <AboutNotifyButton isSidebarOpen={isSidebarOpen} />

                        {/* Logout Button
                        <button
                            onClick={handleLogout}
                            className={`flex items-center rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all ${!isSidebarOpen ? 'h-10 w-10 mx-auto justify-center' : 'w-full h-11 px-3 gap-3'
                                }`}
                            title={!isSidebarOpen ? 'Sign Out' : ''}
                        >
                            <FiLogOut className="w-5 h-5 flex-shrink-0" />
                            {isSidebarOpen && (
                                <span className="font-medium text-sm whitespace-nowrap">Sign Out</span>
                            )}
                        </button> */}
                    </div>
                </div>
            </aside>

            {/* Mobile Bottom Navigation */}
            <div className="mobile-bottom-nav md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-zinc-900 border-t border-gray-200 dark:border-zinc-800 safe-area-inset-bottom">
                <div className="flex justify-around items-center px-2 py-2">
                    {/* Primary navigation items */}
                    {getPrimaryNavItems().map((item) => {
                        const Icon = item.icon;
                        const isActive = isActivePath(item.path);

                        return (
                            <button
                                key={item.path}
                                onClick={() => navigate(item.path)}
                                disabled={item?.disabled}
                                className={`flex flex-col items-center justify-center min-w-[64px] min-h-[56px] px-2 py-1 rounded-lg transition-all ${isActive
                                    ? `${userStyle.lightBg} ${userStyle.text}`
                                    : 'text-gray-600 dark:text-gray-400'
                                    } ${item?.disabled && 'opacity-50 cursor-not-allowed'}`}
                            >
                                <Icon className={`w-6 h-6 mb-1 ${isActive ? userStyle.text : ''}`} />
                                <span className="text-[10px] font-medium truncate max-w-full">
                                    {item.name}
                                </span>
                            </button>
                        );
                    })}

                    {/* More menu button */}
                    <button
                        aria-label="More navigation"
                        aria-expanded={isMobileMenuOpen}
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className={`flex flex-col items-center justify-center min-w-[64px] min-h-[56px] px-2 py-1 rounded-lg transition-all ${isMobileMenuOpen
                            ? `${userStyle.lightBg} ${userStyle.text}`
                            : 'text-gray-600 dark:text-gray-400'
                            }`}
                    >
                        <FiMoreHorizontal className={`w-6 h-6 mb-1 ${isMobileMenuOpen ? userStyle.text : ''}`} />
                        <span className="text-[10px] font-medium">More</span>
                    </button>
                </div>
            </div>

            {/* Mobile Full-Screen Menu */}
            {isMobileMenuOpen && (
                <div className="mobile-more-menu md:hidden fixed inset-0 z-[60] bg-white dark:bg-zinc-900">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-800">
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => {
                                    setUserDetailsOpen(true)
                                }} // navigate(`/${companySlug}/user/${userProfile?.username?.toLowerCase() || userProfile?.id}`)
                                className={`flex items-center rounded-xl hover:bg-zinc-700 dark:hover:bg-zinc-800 transition-all ${!isSidebarOpen ? 'h-12 w-10 mx-auto justify-center' : 'w-full h-14 px-3 gap-3'
                                    }`}
                            >
                                <div className={`w-10 h-10 ${userStyle.bg} rounded-full flex items-center justify-center flex-shrink-0 shadow-lg ${userProfile?.status !== "active" && "bg-gradient-to-br from-gray-500 to-gray-600"}`}>
                                    {userProfile?.avatar_url ? (
                                        <img
                                            src={userProfile.avatar_url}
                                            alt={userProfile.full_name || userProfile.username}
                                            className="w-10 h-10 rounded-full object-cover"
                                        />
                                    ) : (
                                        <FiUser className="w-5 h-5 text-white" />
                                    )}
                                </div>
                                <div className={`flex-1 text-left min-w-0 overflow-hidden transition-all duration-300 ${isSidebarOpen ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0'
                                    }`}>
                                    <div className="font-semibold text-zinc-100 dark:text-white truncate text-sm">
                                        {userProfile?.full_name || userProfile?.username}
                                    </div>
                                    <div className={`text-xs font-medium ${userStyle.text} uppercase truncate ${userProfile?.status !== "active" && "text-gray-500"}`}>
                                        {userProfile?.status !== "active" ?
                                            userProfile?.status
                                            :
                                            userProfile?.role?.split("_")?.join(" ") || "USER"
                                        }
                                    </div>
                                </div>
                            </button>
                            <div>
                                <div className="font-semibold text-gray-900 dark:text-white text-sm">
                                    {userProfile?.full_name || userProfile?.username}
                                </div>
                                <div className={`text-xs font-medium ${userStyle.text} uppercase`}>
                                    {userProfile?.role?.split("_")?.join(" ") || "USER"}
                                </div>
                            </div>
                        </div>
                        <button
                            aria-label="Close navigation"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                        >
                            <FiX className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                        </button>
                    </div>

                    {/* Company Info */}
                    <div className="p-4 border-b border-gray-200 dark:border-zinc-800">
                        <div className="flex items-center space-x-3">
                            {company?.logo_url ? (
                                <img
                                    src={company.logo_url}
                                    alt={company.name}
                                    className="w-10 h-10 rounded-lg object-cover"
                                />
                            ) : (
                                <div className={`w-10 h-10 ${userStyle.bg} rounded-lg flex items-center justify-center`}>
                                    <span className="text-white font-semibold text-sm">
                                        {company?.name?.charAt(0) || 'C'}
                                    </span>
                                </div>
                            )}
                            <div>
                                <div className="font-semibold text-gray-900 dark:text-white text-sm">
                                    {company?.name || 'Company'}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {company?.slug || 'company'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable Menu Content */}
                    <div className="flex-1 overflow-y-auto pb-32">
                        {/* All Navigation Items */}
                        <div className="p-4 space-y-2">
                            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                Navigation
                            </h3>
                            {navigationItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = isActivePath(item.path);

                                return (
                                    <button
                                        key={item.path}
                                        onClick={() => {
                                            navigate(item.path);
                                            setIsMobileMenuOpen(false);
                                        }}
                                        disabled={item?.disabled}
                                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${isActive
                                            ? `${userStyle.lightBg} ${userStyle.text}`
                                            : 'tezt-zinc-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800'
                                            } ${item?.disabled && 'opacity-50 cursor-not-allowed'}`}
                                    >
                                        <Icon className="w-5 h-5 flex-shrink-0" />
                                        <span className="font-medium text-sm">{item.name}</span>
                                        {isActive && (
                                            <div className={`ml-auto w-2 h-2 rounded-full ${userStyle.bg}`}></div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Messages & Notifications */}
                        <div className="p-4 space-y-2 border-t border-gray-200 dark:border-zinc-800">
                            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                Communication
                            </h3>

                            <button
                                onClick={() => {
                                    if (userProfile?.status === "active") {
                                        setShowMessageMenu(true);
                                        setIsMobileMenuOpen(false);
                                    }
                                }}
                                disabled={userProfile?.status !== "active"}
                                className={`w-full text-start flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${showMessageMenu
                                    ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600'
                                    : unreadMessageCount > 0
                                        ? 'bg-purple-50/50 dark:bg-purple-900/10 text-purple-600 dark:text-purple-400'
                                        : 'tezt-zinc-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800'
                                    } ${userProfile?.status !== "active" && 'opacity-50 cursor-not-allowed'}`}
                            >
                                <div className="relative">
                                    <FiMessageSquare className={`w-5 h-5 ${unreadMessageCount > 0 ? 'msg-icon-buzz' : ''}`} />
                                    {unreadMessageCount > 0 && (
                                        <span className="msg-badge-breathe absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                            {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                                        </span>
                                    )}
                                </div>
                                <span className="font-medium text-sm flex-1">Messages</span>
                                {unreadMessageCount > 0 && (
                                    <span className="px-2 py-0.5 bg-red-600 text-white text-xs rounded-full font-bold msg-badge-breathe">
                                        {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                                    </span>
                                )}
                            </button>

                            <button
                                onClick={() => {
                                    if (userProfile?.status === "active") {
                                        setShowNotificationMenu(true);
                                        setIsMobileMenuOpen(false);
                                    }
                                }}
                                disabled={userProfile?.status !== "active"}
                                className={`w-full text-start flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${showNotificationMenu
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600'
                                    : unreadNotificationCount > 0
                                        ? 'bg-blue-50/50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400'
                                        : 'tezt-zinc-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800'
                                    } ${userProfile?.status !== "active" && 'opacity-50 cursor-not-allowed'}`}
                            >
                                <div className="relative">
                                    <FiBell className={`w-5 h-5 ${unreadNotificationCount > 0 ? 'notif-icon-buzz' : ''}`} />
                                    {unreadNotificationCount > 0 && (
                                        <span className="notif-badge-breathe absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                            {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                                        </span>
                                    )}
                                </div>
                                <span className="font-medium text-sm flex-1">Notifications</span>
                                {unreadNotificationCount > 0 && (
                                    <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full font-bold notif-badge-breathe">
                                        {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                                    </span>
                                )}
                            </button>
                        </div>




                        {/* Logout */}
                        <div className="p-4 flex flex-col border-t border-gray-200 dark:border-zinc-800 gap-2">
                            <button
                                onClick={() => {
                                    setUserDetailsOpen(true)
                                }} // navigate(`/${companySlug}/user/${userProfile?.username?.toLowerCase() || userProfile?.id}`)
                                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl  dark:bg-red-900/20 border border-zinc-200 transition-all'
                                    }`}
                            >
                                <FiUser className="w-5 h-5" />
                                <span className="font-medium text-sm">User Settings</span>
                            </button>
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
                            >
                                <FiLogOut className="w-5 h-5" />
                                <span className="font-medium text-sm">Sign Out</span>
                            </button>
                        </div>

                        {/* Branding */}
                        <div className="p-4 text-center">
                            <a href="https://notifyapp.com.au" target="_blank" rel="noopener noreferrer">
                                <img
                                    className="w-32 mx-auto opacity-50"
                                    src={process.env.PUBLIC_URL + '/assets/notify-cropped-wide.png'}
                                    alt="Notify"
                                />
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Message and Notification Menus */}
            <MessageMenu
                isOpen={showMessageMenu}
                onOpen={() => setShowMessageMenu(true)}
                onClose={() => setShowMessageMenu(false)}
                onRefreshBadge={refreshMessageBadge}
                isSidebarOpen={isSidebarOpen}
            />
            <NotificationMenu
                isOpen={showNotificationMenu}
                onClose={() => setShowNotificationMenu(false)}
                onRefreshBadge={refreshNotificationBadge}
                isSidebarOpen={isSidebarOpen}
                latestNotification={latestNotification}
                onSoundSettingsChange={updateNotificationSoundSettings}
            />


            {/* Mobile overlay - only for mobile menu */}
            {isMobileMenuOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Desktop overlay */}
            {isSidebarOpen && (
                <div
                    className="hidden md:block fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* ── Message notification animations ───────────────────────────── */}
            <style>{`
                @keyframes msg-breathe {
                    0%, 100% { transform: scale(1);   opacity: 1; }
                    50%       { transform: scale(1.35); opacity: 0.75; }
                }
                /* Buzz for 0.8 s every 4 s — achieved by stretching the iteration to 4 s
                   and putting all the action in the first 20% (0–0.8 s) */
                @keyframes msg-buzz-cycle {
                    0%   { transform: rotate(0deg); }
                    2%   { transform: rotate(-12deg); }
                    4%   { transform: rotate(12deg); }
                    6%   { transform: rotate(-10deg); }
                    8%   { transform: rotate(10deg); }
                    10%  { transform: rotate(-6deg); }
                    12%  { transform: rotate(6deg); }
                    16%  { transform: rotate(-3deg); }
                    20%  { transform: rotate(0deg); }
                    100% { transform: rotate(0deg); }
                }
                .msg-icon-buzz {
                    animation: msg-buzz-cycle 4s ease-in-out infinite;
                }
                .msg-badge-breathe {
                    animation: msg-breathe 1.6s ease-in-out infinite;
                }
                @keyframes msg-toast-pop {
                    0%   { opacity: 0; transform: translateX(-14px) scale(0.92); }
                    60%  { opacity: 1; transform: translateX(4px)   scale(1.03); }
                    100% { opacity: 1; transform: translateX(0)     scale(1); }
                }
                .msg-toast-enter {
                    animation: msg-toast-pop 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards;
                }
                /* ── Notification bell animations ── */
                @keyframes notif-buzz-cycle {
                    0%   { transform: rotate(0deg); }
                    2%   { transform: rotate(-15deg); }
                    4%   { transform: rotate(15deg); }
                    6%   { transform: rotate(-12deg); }
                    8%   { transform: rotate(12deg); }
                    10%  { transform: rotate(-8deg); }
                    12%  { transform: rotate(8deg); }
                    16%  { transform: rotate(-3deg); }
                    20%  { transform: rotate(0deg); }
                    100% { transform: rotate(0deg); }
                }
                .notif-icon-buzz {
                    animation: notif-buzz-cycle 4s ease-in-out infinite;
                    transform-origin: top center;
                }
                .notif-badge-breathe {
                    animation: msg-breathe 1.6s ease-in-out infinite;
                }
            `}</style>

            {/* ── New-message toast bubble ──────────────────────────────────── */}
            
            {/* ── New-notification toast bubble ─────────────────────────────── */}
            {latestNotification && (
                <div
                    className="msg-toast-enter fixed z-[200] flex items-center gap-0 cursor-pointer"
                    style={isMobile || !notifButtonRect ? {
                        left: '1rem',
                        bottom: '5rem',
                        maxWidth: '300px',
                    } : {
                        left: `${notifButtonRect.right + 8}px`,
                        top: `${notifButtonRect.top + notifButtonRect.height / 2}px`,
                        transform: 'translateY(-50%)',
                        maxWidth: '300px',
                    }}
                    onClick={() => {
                        dismissLatestNotification();
                        setShowNotificationMenu(true);
                        setShowMessageMenu(false);
                    }}
                >
                    {/* Arrow pointer pointing LEFT toward the notification button */}
                    {!isMobile && notifButtonRect && (
                        <div
                            style={{
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderRight: '10px solid #3b82f6',
                                flexShrink: 0,
                            }}
                        />
                    )}

                    {/* Bubble body */}
                    <div className="relative flex items-start gap-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-2xl shadow-blue-500/40 px-4 py-3 text-white">
                        {/* Bell icon */}
                        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                            <FiBell className="w-4 h-4 text-white" />
                        </div>

                        {/* Text */}
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold leading-tight">
                                You got a new notification!
                            </p>
                            {latestNotification.title && (
                                <p className="text-xs text-blue-100 truncate mt-0.5">
                                    {latestNotification.title}
                                </p>
                            )}
                            <p className="text-[10px] text-blue-200 mt-1 leading-tight">
                                Tap to open →
                            </p>
                        </div>

                        {/* Dismiss × */}
                        <button
                            onClick={(e) => { e.stopPropagation(); dismissLatestNotification(); }}
                            className="flex-shrink-0 w-5 h-5 rounded-full bg-white/20 hover:bg-white/30 transition-colors flex items-center justify-center text-white text-xs font-bold leading-none"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {userDetailsOpen &&
                <UserDetails
                    modalUserId={userProfile?.id}
                    onClose={(deleted) => {
                        setUserDetailsOpen(false);
                    }}
                />
            }

        </>
    );
};

export default Navbar;
